(() => {
  const appearance = window.BinderPullsAppearance;
  const switcher = document.querySelector('.bp-theme-switcher');
  if (!appearance || !switcher) return;
  const tab = switcher.querySelector('.bp-theme-tab');
  const panel = switcher.querySelector('.bp-theme-panel');
  const close = (restoreFocus = false) => {
    panel.hidden = true;
    tab.setAttribute('aria-expanded', 'false');
    if (restoreFocus) tab.focus();
  };
  const syncAppearance = () => {
    const { bpTheme: mode, bpPreference: preference } = document.documentElement.dataset;
    switcher.querySelectorAll('input[name="bp-appearance"]').forEach(input => {
      input.checked = input.value === preference;
    });
    const status = preference === 'auto' ? `auto-${mode}` : mode;
    switcher.querySelectorAll('[data-bp-status]').forEach(element => {
      const hidden = element.dataset.bpStatus !== status;
      if (element.hidden !== hidden) element.hidden = hidden;
    });
  };
  tab.addEventListener('click', () => {
    if (!panel.hidden) return close(true);
    panel.hidden = false;
    tab.setAttribute('aria-expanded', 'true');
    panel.querySelector('input:checked')?.focus();
  });
  switcher.querySelector('.bp-theme-close').addEventListener('click', () => close(true));
  switcher.addEventListener('change', event => {
    if (event.target.matches('input[name="bp-appearance"]')) appearance.set(event.target.value);
  });
  switcher.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || panel.hidden) return;
    event.preventDefault();
    event.stopPropagation();
    close(true);
  });
  switcher.addEventListener('focusout', event => {
    if (event.relatedTarget && !switcher.contains(event.relatedTarget)) close();
  });
  document.addEventListener('click', event => {
    if (!switcher.contains(event.target)) close();
  });
  document.addEventListener('bp:appearance-change', syncAppearance);
  document.addEventListener('shopify:section:load', syncAppearance);
  switcher.hidden = false;
  syncAppearance();
})();

if (!customElements.get('bp-arrivals')) {
  customElements.define('bp-arrivals', class extends HTMLElement {
    connectedCallback() {
      this.setupRows();
      if (this.initialized) return;
      this.initialized = true;
      this.tabs = [...this.querySelectorAll('[data-bp-tab]')];
      this.panels = [...this.querySelectorAll('[data-bp-panel]')];
      if (!this.tabs.length) return;
      this.querySelector('[data-bp-tabs]').setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[index].id);
        this.panels[index].setAttribute('role', 'tabpanel');
        this.panels[index].setAttribute('aria-labelledby', tab.id);
        tab.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.select(index);
        });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        });
      });
      this.addEventListener('shopify:block:select', event => {
        const index = this.tabs.findIndex(tab => tab.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index);
      });
      this.select(0);
    }
    setupRows() {
      this.rowEvents?.abort();
      this.rowEvents = new AbortController();
      const desktop = matchMedia('(min-width: 990px)');
      const mobile = matchMedia('(max-width: 749px)');
      const grids = [...this.querySelectorAll('[data-bp-row-grid]')].map(grid => ({ grid, cards: [...grid.children] }));
      const arrange = () => {
        for (const { grid, cards } of grids) {
          const columns = desktop.matches ? 4 : 2;
          const queues = ['Graded single', 'Raw single', 'Other'].map(group => cards.filter(card => card.dataset.bpFormat === group));
          let position = 0;
          while (queues.some(queue => queue.length)) {
            for (const queue of queues) {
              for (const [index, card] of queue.splice(0, columns).entries()) {
                card.toggleAttribute('data-bp-row-start', index === 0);
                card.classList.remove('bp-desktop-card');
                card.hidden = mobile.matches && position >= Number(this.dataset.mobileLimit || cards.length);
                grid.append(card);
                position += 1;
              }
            }
          }
        }
      };
      desktop.addEventListener('change', arrange, { signal: this.rowEvents.signal });
      mobile.addEventListener('change', arrange, { signal: this.rowEvents.signal });
      arrange();
    }
    disconnectedCallback() {
      this.rowEvents?.abort();
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
    }
  });
}

// Keep every shared card header aligned without truncating long catalog names.
// Measure the content within each grid so unrelated sections cannot add space.
(() => {
  const observed = new Set();
  const widths = new WeakMap();
  let frame = 0;
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(equalize);
  };
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
    for (const entry of entries) {
      const width = entry.contentRect.width;
      if (widths.get(entry.target) !== width) {
        widths.set(entry.target, width);
        schedule();
      }
    }
  }) : null;
  function equalize() {
    frame = 0;
    for (const label of observed) {
      if (!label.isConnected) {
        observer?.unobserve(label);
        observed.delete(label);
      }
    }
    const labels = [...document.querySelectorAll('.bp-product-card > .bp-card-label')];
    for (const label of labels) {
      if (!observed.has(label)) {
        observed.add(label);
        observer?.observe(label);
      }
    }
    const grids = new Map();
    for (const label of labels) {
      const grid = label.closest('.bp-product-grid, .product-grid')
        || label.closest('ul, ol') || label.parentElement.parentElement;
      if (!grids.has(grid)) grids.set(grid, []);
      grids.get(grid).push(label);
    }
    // Reset every grid first: a previous measurement must not become a minimum.
    for (const grid of grids.keys()) grid.style.setProperty('--bp-card-label-height', '0px');
    for (const [grid, gridLabels] of grids) {
      let tallest = 0;
      for (const label of gridLabels) {
        const bounds = label.getBoundingClientRect();
        if (bounds.width > 0 && bounds.height > 0) tallest = Math.max(tallest, bounds.height);
      }
      grid.style.setProperty('--bp-card-label-height', `${Math.ceil(tallest)}px`);
    }
  }
  function start() {
    schedule();
    new MutationObserver(schedule).observe(document.body, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['hidden']
    });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:load', schedule);
    document.fonts?.ready.then(schedule);
    document.fonts?.addEventListener('loadingdone', schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
